import { parseCsv, stringifyCsv } from '@/utils/csv';

export const BULK_DELETE_MATTERS_COLUMN = 'case_reference';

export const BULK_DELETE_MATTERS_TEMPLATE_FILE_NAME = 'bulk_delete_matters_template.csv';

export const BULK_DELETE_MATTERS_TEMPLATE_CSV = stringifyCsv([[BULK_DELETE_MATTERS_COLUMN], ['123456g1']]);

/** Headers `matters/bulk-delete` reads as `case_reference`, compared ignoring case. */
const HEADER_ALIASES = [BULK_DELETE_MATTERS_COLUMN, 'case reference', 'reference'];

/** `matters/bulk-delete` takes the CSV in the query string (~40k chars max), so ask the user to split larger lists. */
const MAX_ENCODED_CSV_LENGTH = 30_000;

/** Keeps a pasted paragraph from swamping the list of problems. */
const MAX_QUOTED_VALUE_LENGTH = 40;

export interface BulkDeleteMattersRowError {
  /** As a spreadsheet numbers it: the header is row 1. */
  rowNumber: number;
  problems: string[];
}

export type BulkDeleteMattersValidationResult =
  | { isValid: true; rowCount: number; references: string[]; csv: string }
  | { isValid: false; error: string; rowErrors: BulkDeleteMattersRowError[] };

const listError = (error: string): BulkDeleteMattersValidationResult => ({ isValid: false, error, rowErrors: [] });

const quote = (value: string) =>
  `"${value.length > MAX_QUOTED_VALUE_LENGTH ? `${value.slice(0, MAX_QUOTED_VALUE_LENGTH)}…` : value}"`;

/** The backend trims and lowercases each reference before looking it up. */
const normaliseReference = (value: string) => value.trim().toLowerCase();

const toResult = (references: string[]): BulkDeleteMattersValidationResult => {
  const csvToSend = stringifyCsv([[BULK_DELETE_MATTERS_COLUMN], ...references.map((reference) => [reference])]);

  if (encodeURIComponent(csvToSend).length > MAX_ENCODED_CSV_LENGTH) {
    return listError('That is too many case references to delete in one go. Please split them into smaller batches.');
  }

  return { isValid: true, rowCount: references.length, references, csv: csvToSend };
};

/**
 * Applies the same rules as `matters/bulk-delete`, so problems show per row before anything is sent.
 * A valid result carries the CSV to send, with the references normalised as the server reads them.
 */
export const validateBulkDeleteMattersCsv = (csv: string): BulkDeleteMattersValidationResult => {
  const rows = parseCsv(csv.replace(/^\uFEFF/, '')).map((row) => row.map((cell) => cell.trim()));
  const headerIndex = rows.findIndex((row) => row.some(Boolean));

  if (headerIndex === -1) {
    return listError('The selected file is empty.');
  }

  const columnIndex = rows[headerIndex].findIndex((column) => HEADER_ALIASES.includes(column.toLowerCase()));

  if (columnIndex === -1) {
    return listError(
      `The file is missing the required column: ${BULK_DELETE_MATTERS_COLUMN}. Download the template and try again.`,
    );
  }

  const entries = rows
    .map((row, index) => ({ row, rowNumber: index + 1 }))
    .slice(headerIndex + 1)
    .filter(({ row }) => row.some(Boolean));

  if (entries.length === 0) {
    return listError('The file does not contain any case references.');
  }

  const firstRowByReference = new Map<string, number>();
  const rowErrors: BulkDeleteMattersRowError[] = [];

  entries.forEach(({ row, rowNumber }) => {
    const rawReference = row[columnIndex] ?? '';
    const reference = normaliseReference(rawReference);

    if (!reference) {
      rowErrors.push({ rowNumber, problems: [`${BULK_DELETE_MATTERS_COLUMN} is empty`] });
    } else if (firstRowByReference.has(reference)) {
      rowErrors.push({
        rowNumber,
        problems: [`${quote(rawReference)} is already listed on row ${firstRowByReference.get(reference)}`],
      });
    } else {
      firstRowByReference.set(reference, rowNumber);
    }
  });

  if (rowErrors.length > 0) {
    const verb = rowErrors.length === 1 ? 'needs' : 'need';

    return {
      isValid: false,
      error: `${rowErrors.length} of ${entries.length} row${entries.length > 1 ? 's' : ''} ${verb} fixing. Update the file and upload it again.`,
      rowErrors,
    };
  }

  return toResult([...firstRowByReference.keys()]);
};

export type PastedCaseReferencesResult = BulkDeleteMattersValidationResult & {
  /** References that appeared more than once and were sent only once. */
  duplicates: string[];
};

/**
 * Reads references pasted one per line, as a comma/semicolon list, or as a column copied from a
 * spreadsheet. Repeats are dropped rather than rejected, since there's no row to point at.
 */
export const parsePastedCaseReferences = (text: string): PastedCaseReferencesResult => {
  const references = new Set<string>();
  const duplicates = new Set<string>();

  const lines = text.split(/\r?\n/);

  // A column copied from a spreadsheet may bring its header along.
  if (HEADER_ALIASES.includes(lines[0].trim().toLowerCase())) {
    lines.shift();
  }

  lines
    .join('\n')
    .split(/[\s,;]+/)
    .map(normaliseReference)
    .filter(Boolean)
    .forEach((reference) => {
      if (references.has(reference)) {
        duplicates.add(reference);
      } else {
        references.add(reference);
      }
    });

  const result = references.size === 0 ? listError('Paste at least one case reference.') : toResult([...references]);

  return { ...result, duplicates: [...duplicates] };
};
